import { useTranslation } from 'react-i18next';
import i18n from './i18n';

type TranslationFunction = ReturnType<typeof useTranslation>['t'];

export const DECIMAL_INPUT_REGEX = /^-?\d*(?:[.,]\d*)?$/;
export const DECIMAL_TWO_PLACES_INPUT_REGEX = /^-?\d*(?:[.,]\d{0,2})?$/;

const COUNTER_INPUT_SANITIZE_REGEX = /^-?\d*(?:[.,]\d*)?$/;

const getNumberLocale = () => (i18n.language === 'pl' ? 'pl-PL' : 'en-US');

const normalizeCounterNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;

  const sanitized = String(value)
    .trim()
    .replace(/[\s\u00A0\u202F]/g, "")
    .replace(/,/g, ".");

  if (!COUNTER_INPUT_SANITIZE_REGEX.test(sanitized)) return null;

  const integerPart = sanitized.split(".")[0];
  if (integerPart === "" || integerPart === "-") return null;

  const parsed = Number(integerPart);
  return Number.isFinite(parsed) ? parsed : null;
};

export const formatCounterProvider = (
  providerDisplay?: string | null,
  emailSourceDisplay?: string | null,
): string => {
  // When the email source (PDF / counter report) is known, it's more specific
  // than the generic "Automatic email" provider label, so show it instead.
  if (emailSourceDisplay) return emailSourceDisplay;

  return providerDisplay || "-";
};

export const formatNumber = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "-";

  const numValue = Number(value);
  if (Number.isNaN(numValue)) return String(value);

  const locale = getNumberLocale();

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numValue);
};

// Counter values are stored as decimals for future-proofing, but are always
// whole numbers in practice today - display them without decimal places.
export const formatCounterValue = (value: unknown): string => {
  const normalizedValue = normalizeCounterNumber(value);
  if (normalizedValue === null) return value === null || value === undefined || value === "" ? "-" : String(value);

  return new Intl.NumberFormat(getNumberLocale(), { maximumFractionDigits: 0 }).format(normalizedValue);
};

export const normalizeCounterInputValue = (value: string): string | null => {
  const sanitized = value.replace(/[\s\u00A0\u202F]/g, "").replace(/,/g, ".");
  if (!COUNTER_INPUT_SANITIZE_REGEX.test(sanitized)) return null;

  const sign = sanitized.startsWith("-") ? "-" : "";
  const unsignedValue = sign ? sanitized.slice(1) : sanitized;
  const integerPart = unsignedValue.split(".")[0].replace(/\D/g, "");

  if (!integerPart) return "";

  return `${sign}${integerPart}`;
};

export const formatCounterInputValue = (value: string): string => {
  if (value === "") return value;

  return formatCounterValue(value);
};

export const getMonthNames = (t: TranslationFunction): Record<number, string> => ({
  1: t("January"),
  2: t("February"),
  3: t("March"),
  4: t("April"),
  5: t("May"),
  6: t("June"),
  7: t("July"),
  8: t("August"),
  9: t("September"),
  10: t("October"),
  11: t("November"),
  12: t("December"),
});

/** Form-only sentinel; backend expands to Mon–Fri and never stores this value. */
export const WORKING_DAYS_WEEK_DAY = "0";

export const WEEK_DAYS = [
  { value: "1", labelKey: "Monday" },
  { value: "2", labelKey: "Tuesday" },
  { value: "3", labelKey: "Wednesday" },
  { value: "4", labelKey: "Thursday" },
  { value: "5", labelKey: "Friday" },
  { value: "6", labelKey: "Saturday" },
  { value: "7", labelKey: "Sunday" },
];

export const OPENING_HOUR_WEEK_DAY_OPTIONS = [
  { value: WORKING_DAYS_WEEK_DAY, labelKey: "Working days (Mon-Fri)" },
  ...WEEK_DAYS,
];

export const formatDateTime = (value: unknown): string => {
  if (!value) return '';
  const date = new Date(value as string);
  if (Number.isNaN(date.getTime())) return value as string;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}\n${hours}:${minutes}`;
};

export const formatDate = (value: unknown): string => {
  if (!value) return '';
  const date = new Date(value as string);
  if (Number.isNaN(date.getTime())) return value as string;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${day}-${month}-${year}`;
};

export const formatDateTimeBadge = (value: unknown) => {
  const formatted = formatDateTime(value);
  if (!formatted) return '';

  const [date, time] = formatted.split('\n');
  if (!time) return formatted;

  return (
    <span
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 1,
        padding: '2px 6px',
        borderRadius: 8,
        background: 'var(--mui-palette-action-hover)',
        border: '1px solid var(--mui-palette-divider)',
        lineHeight: 1.15,
      }}
    >
      <span style={{ fontWeight: 600, fontSize: 12 }}>{date}</span>
      <span style={{ fontSize: 10, color: 'var(--mui-palette-text-secondary)' }}>{time}</span>
    </span>
  );
};

export const highlightMatch = (text: string, highlight: string) => {
  if (!highlight) return text;

  const parts = text.split(new RegExp(`(${highlight})`, "gi"));
  return (
    <span>
      {parts.map((part, index) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <strong key={index}>{part}</strong>
        ) : (
          <span key={index}>{part}</span>
        )
      )}
    </span>
  );
};

export const formatAddress = (addr: any) => {
  if (!addr) return null;

  const parts = [
    addr.street,
    addr.city,
    addr.zip_code,
  ].filter(Boolean);

  return parts.join(", ");
};

export const getContrastColor = (hex: string): string => {
  let color = hex.replace("#", "");
  // Expand 3-digit hex to 6-digit
  if (color.length === 3) {
    color = color.split("").map((c) => c + c).join("");
  }
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return "#000000";
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? "#000000" : "#ffffff";
};
